import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import {
  BarChart3, TrendingUp, FlaskConical, Globe2, Brain,
  GraduationCap, Filter, ChevronDown, ChevronUp, Loader2,
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import api from '../utils/api';

/* ── Tooltip style ─────────────────────────────────────────────────────────── */
const TT = {
  contentStyle: {
    background: '#0f0d36',
    border: '1px solid rgba(245,197,24,0.2)',
    borderRadius: 12,
    color: '#fff',
    fontSize: 12,
  },
};

/* ── KPI pill ──────────────────────────────────────────────────────────────── */
const StatPill = ({ label, value, delta, color }) => (
  <div className={`rounded-xl p-4 ${color}`}>
    <div className="font-display font-bold text-2xl mb-0.5">{value}</div>
    <div className="text-xs font-medium opacity-80">{label}</div>
    {delta && <div className="text-[10px] mt-1 opacity-60">↑ {delta} vs last year</div>}
  </div>
);

/* ── Chart card ────────────────────────────────────────────────────────────── */
const ChartCard = ({ title, subtitle, children, className = '' }) => (
  <div className={`bg-white rounded-2xl shadow-card border border-gray-100 p-6 ${className}`}>
    <div className="mb-5">
      <h3 className="font-display font-semibold text-gray-900">{title}</h3>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
    {children}
  </div>
);

/* ── Embed card for Power BI / Tableau / Streamlit ─────────────────────────── */
const EmbedCard = ({ title, description, embedUrl, placeholder, icon: Icon }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
      <div className="flex items-start justify-between p-6 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 bg-royal-50 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon size={20} className="text-royal-800" />
          </div>
          <div>
            <h3 className="font-display font-semibold text-gray-900 mb-1">{title}</h3>
            <p className="text-sm text-gray-500 leading-relaxed">{description}</p>
          </div>
        </div>
        <button className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0 ml-4">
          {expanded ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
        </button>
      </div>
      {expanded && (
        <div className="px-6 pb-6">
          {embedUrl ? (
            <div className="rounded-xl overflow-hidden border border-gray-200" style={{ height: 500 }}>
              <iframe src={embedUrl} title={title} className="w-full h-full" frameBorder="0" allowFullScreen />
            </div>
          ) : (
            <div className="rounded-xl bg-gray-50 border-2 border-dashed border-gray-200 flex flex-col items-center justify-center p-12 text-center" style={{ minHeight: 260 }}>
              <Icon size={40} className="text-gray-300 mb-4" />
              <h4 className="font-semibold text-gray-500 mb-2">{placeholder}</h4>
              <p className="text-xs text-gray-400 max-w-sm leading-relaxed">
                Paste a Power BI, Tableau Public, or Streamlit embed URL in Admin → Analytics to display this interactive dashboard.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Render a dataset as the appropriate chart ─────────────────────────────── */
const DatasetChart = ({ dataset }) => {
  const { chartType, dataPoints, seriesLabels, seriesColors } = dataset;
  if (!dataPoints || dataPoints.length === 0) return <div className="text-center text-gray-400 text-sm py-8">No data points yet.</div>;

  const c0 = seriesColors?.[0] || '#2B3A8F';
  const c1 = seriesColors?.[1] || '#F5C518';
  const c2 = seriesColors?.[2] || '#10b981';

  const l0 = seriesLabels?.[0] || 'Value';
  const l1 = seriesLabels?.[1] || 'Value 2';
  const l2 = seriesLabels?.[2] || 'Value 3';

  if (chartType === 'pie') {
    return (
      <ResponsiveContainer width="100%" height={280}>
        <PieChart>
          <Pie data={dataPoints.map(p => ({ name: p.label, value: p.value, color: p.color || c0 }))}
            cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={3} dataKey="value">
            {dataPoints.map((entry, i) => <Cell key={i} fill={entry.color || seriesColors?.[i] || c0} />)}
          </Pie>
          <Tooltip {...TT} formatter={v => `${v}%`} />
          <Legend iconType="circle" iconSize={10} formatter={v => <span style={{ fontSize: 11 }}>{v}</span>} />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'horizontal_bar') {
    const hasSecond = dataPoints.some(p => p.value2 != null);
    return (
      <ResponsiveContainer width="100%" height={Math.max(220, dataPoints.length * 42)}>
        <BarChart data={dataPoints.map(p => ({ name: p.label, [l0]: p.value, ...(hasSecond ? { [l1]: p.value2 } : {}), color: p.color }))} layout="vertical">
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11 }} domain={hasSecond ? [0, 100] : undefined} tickFormatter={hasSecond ? v => `${v}%` : undefined} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={110} />
          <Tooltip {...TT} formatter={hasSecond ? v => `${v}%` : undefined} />
          {hasSecond && <Legend />}
          <Bar dataKey={l0} fill={hasSecond ? '#e2e8f0' : undefined} radius={hasSecond ? [0,4,4,0] : [0,6,6,0]}>
            {!hasSecond && dataPoints.map((entry, i) => <Cell key={i} fill={entry.color || c0} />)}
          </Bar>
          {hasSecond && <Bar dataKey={l1} fill={c1} radius={[0,4,4,0]} />}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'area') {
    const has2 = dataPoints.some(p => p.value2 != null);
    const has3 = dataPoints.some(p => p.value3 != null);
    return (
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={dataPoints.map(p => ({ name: p.label, [l0]: p.value, [l1]: p.value2, [l2]: p.value3 }))}>
          <defs>
            {[['a', c0], ['b', c1], ['c', c2]].map(([id, color]) => (
              <linearGradient key={id} id={`grad-${id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={color} stopOpacity={0.15} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip {...TT} />
          <Legend />
          <Area type="monotone" dataKey={l0} stroke={c0} fill="url(#grad-a)" strokeWidth={2} />
          {has2 && <Area type="monotone" dataKey={l1} stroke={c1} fill="url(#grad-b)" strokeWidth={2} />}
          {has3 && <Area type="monotone" dataKey={l2} stroke={c2} fill="none" strokeWidth={2} strokeDasharray="4 2" />}
        </AreaChart>
      </ResponsiveContainer>
    );
  }

  if (chartType === 'line') {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={dataPoints.map(p => ({ name: p.label, [l0]: p.value }))}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip {...TT} />
          <Line type="monotone" dataKey={l0} stroke={c0} strokeWidth={2.5}
            dot={{ fill: c1, r: 4 }} activeDot={{ r: 6 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  }

  // Default: grouped bar
  const hasSecond = dataPoints.some(p => p.value2 != null);
  const hasThird  = dataPoints.some(p => p.value3 != null);
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={dataPoints.map(p => ({ name: p.label, [l0]: p.value, [l1]: p.value2, [l2]: p.value3 }))}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip {...TT} />
        {(hasSecond || hasThird) && <Legend />}
        <Bar dataKey={l0} name={l0} fill={c0} radius={[4,4,0,0]} />
        {hasSecond && <Bar dataKey={l1} name={l1} fill={c1} radius={[4,4,0,0]} />}
        {hasThird  && <Bar dataKey={l2} name={l2} fill={c2} radius={[4,4,0,0]} />}
      </BarChart>
    </ResponsiveContainer>
  );
};

/* ── Category meta ─────────────────────────────────────────────────────────── */
const CATS = [
  { key: 'all',       label: 'All Dashboards',     icon: BarChart3 },
  { key: 'education', label: 'Education',           icon: GraduationCap },
  { key: 'research',  label: 'Research',            icon: FlaskConical },
  { key: 'community', label: 'Community',           icon: Globe2 },
];

const CAT_HEADERS = {
  education: { icon: GraduationCap, title: 'Capacity Development Analytics',   sub: 'Teacher training reach and educator competence improvement.' },
  research:  { icon: FlaskConical,  title: 'Research Output Analytics',         sub: 'Publications, policy briefs, and programme reach by department.' },
  community: { icon: Globe2,        title: 'Community Reach Analytics',         sub: 'Participant engagement and geographic distribution.' },
};

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════════════ */
export default function InsightsPage() {
  const [metrics,  setMetrics]  = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    Promise.all([
      api.get('/analytics/metrics?group=headline'),
      api.get('/analytics/datasets'),
    ]).then(([mRes, dRes]) => {
      setMetrics(mRes.data);
      setDatasets(dRes.data);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filteredDatasets = activeFilter === 'all'
    ? datasets
    : datasets.filter(d => d.category === activeFilter);

  // Group datasets by category for section headers
  const byCategory = filteredDatasets.reduce((acc, d) => {
    const cat = d.category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(d);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gray-50 font-body">
      <Navbar />
      <div className="pt-20">

        {/* Hero */}
        <section className="relative bg-hero-gradient py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iIzIwMjA2MCIgc3Ryb2tlLXdpZHRoPSIwLjUiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] " />
          <div className="relative max-w-7xl mx-auto px-6">
            <div className="max-w-3xl">
              <span className="section-tag border-gold-400/30 text-gold-300 bg-gold-500/10 mb-5">
                <BarChart3 size={13} /> Data & Insights Portal
              </span>
              <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                Interactive Data & Analytics Portal
              </h1>
              <p className="text-white/60 text-lg leading-relaxed mb-6">
                Live educational analytics, survey findings, and research outcomes from SAAI Institute's programs across West Africa — updated directly from our admin dashboard.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Africa', 'Live Data', 'Evidence-Based'].map(tag => (
                  <span key={tag} className="px-3 py-1.5 glass rounded-full text-sm text-white/80 font-medium border border-white/20">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* KPI strip */}
        <section className="bg-royal-900 py-10">
          <div className="max-w-7xl mx-auto px-6">
            {loading ? (
              <div className="flex justify-center"><Loader2 size={22} className="animate-spin text-white/40" /></div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {metrics.slice(0, 4).map(m => (
                  <StatPill
                    key={m.key}
                    label={m.label}
                    value={`${m.prefix || ''}${m.value >= 1000 ? m.value.toLocaleString() : m.value}${m.suffix || ''}`}
                    delta={m.delta}
                    color="bg-gold-500/20 text-gold-300"
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Filter bar */}
        <div className="bg-white border-b border-gray-100 sticky top-[72px] z-30">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3 overflow-x-auto scrollbar-hide">
            <Filter size={15} className="text-gray-400 flex-shrink-0" />
            {CATS.map(cat => (
              <button key={cat.key} onClick={() => setActiveFilter(cat.key)}
                className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold capitalize transition-all flex-shrink-0 ${
                  activeFilter === cat.key ? 'bg-royal-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}>
                <cat.icon size={13} />{cat.label}
              </button>
            ))}
            <div className="ml-auto flex-shrink-0 text-xs text-gray-400 whitespace-nowrap">Updated from admin dashboard</div>
          </div>
        </div>

        {/* Charts */}
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6 space-y-12">
            {loading ? (
              <div className="flex flex-col items-center justify-center h-64 gap-3 text-gray-400">
                <Loader2 size={32} className="animate-spin text-royal-800" />
                <p className="text-sm">Loading analytics data...</p>
              </div>
            ) : filteredDatasets.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <BarChart3 size={40} className="mx-auto mb-3 opacity-30" />
                <p>No datasets published for this category yet.</p>
                <p className="text-xs mt-1">Go to Admin → Analytics to add datasets.</p>
              </div>
            ) : (
              Object.entries(byCategory).map(([cat, catDatasets]) => {
                const header = CAT_HEADERS[cat];
                return (
                  <div key={cat}>
                    {header && (
                      <div className="mb-6">
                        <h2 className="font-display text-2xl font-bold text-gray-900 flex items-center gap-2">
                          <header.icon size={22} className="text-royal-800" />
                          {header.title}
                        </h2>
                        <p className="text-gray-500 text-sm mt-1">{header.sub}</p>
                      </div>
                    )}
                    <div className={`grid gap-6 ${catDatasets.length === 1 ? '' : 'lg:grid-cols-2'}`}>
                      {catDatasets.map(dataset => (
                        <ChartCard
                          key={dataset._id}
                          title={dataset.label}
                          subtitle={dataset.description}
                          className={catDatasets.length === 1 ? '' : ''}
                        >
                          <DatasetChart dataset={dataset} />
                        </ChartCard>
                      ))}
                    </div>
                  </div>
                );
              })
            )}

            {/* External embeds (always shown) */}
            {/* {(activeFilter === 'all') && (
              <div>
                <div className="mb-6">
                  <h2 className="font-display text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Brain size={22} className="text-royal-800" /> Advanced Interactive Dashboards
                  </h2>
                  <p className="text-gray-500 text-sm mt-1">
                    External dashboards embedded from Power BI, Tableau Public, or Streamlit. Manage embed URLs in Admin → Analytics.
                  </p>
                </div>
                <div className="space-y-4">
                  <EmbedCard icon={BarChart3} title="Educational Analytics Dashboard (Power BI)"
                    description="Full interactive Power BI report showing learning outcome analytics, school-level performance metrics, and psychometric assessment results across surveyed regions."
                    placeholder="Power BI embed — add your report URL in Admin → Analytics" />
                  <EmbedCard icon={TrendingUp} title="Survey Findings Explorer (Tableau Public)"
                    description="Interactive Tableau dashboard for stakeholders to filter and explore survey data by region, institution type, grade level, and demographic variables."
                    placeholder="Tableau Public embed — add your viz URL in Admin → Analytics" />
                  <EmbedCard icon={FlaskConical} title="Research Data Application (Streamlit)"
                    description="Live Streamlit application demonstrating our ML and NLP analytics pipeline applied to educational datasets — showcasing technical capability to funders."
                    placeholder="Streamlit app embed — add your app URL in Admin → Analytics" />
                </div>
              </div>
            )} */}

            {/* Data disclaimer */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 flex items-start gap-3">
              <BarChart3 size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-blue-800 mb-1">About this data</div>
                <p className="text-xs text-blue-600 leading-relaxed">
                  All statistics are derived from SAAI Institute's program monitoring and evaluation records, participant assessments, and institutional surveys. Data is managed directly from our admin dashboard. For access to full datasets or research collaboration, <a href="/contact" className="underline font-semibold">contact our Research team</a>.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}